import {
    ASSET_CLASS_IDS,
    BROADRIDGE_PLATFORM_NAME,
    CANTON_NETWORK_NAME,
    MEASURE_SLUGS,
    REPURCHASE_AGREEMENTS_ASSET_CLASS_NAME,
} from "~/consts";
import {
    AggregateFunctions,
    AggregateQueryMode,
    ApiQueryEndpoints,
    Filter,
    FilterOperators,
    SortDirections,
} from "~/modules/filters/types";
import { ExportTableNames } from "~/modules/downloads/types";
import {
    TimeSeriesItem,
    TokenTimeseriesColumns,
    TokenTimeseriesGroupBy,
} from "~/modules/token_timeseries/types";
import { VisualizationModeCard } from "~/modules/visualization/components/Timeseries/VisualizationModeCard";
import {
    ASSET_TIMESERIES_V4_API_TOOLS_CONTROLS,
    FUND_FLOWS_TIMESERIES_V4_API_TOOLS_CONTROLS,
    TOKEN_TIMESERIES_V4_API_TOOLS_CONTROLS,
} from "~/modules/visualization/components/Timeseries/consts";
import {
    VisualizationMode,
    VisualizationModeTypes,
} from "~/modules/visualization/components/Timeseries/types";
import { interpolateMissingDates } from "~/modules/visualization/util/interpolateMissingDates";
import {
    DEFAULT_DISTRIBUTION_SELECT_PROPS,
    DEFAULT_GROUP_BY_PROPS,
    DEFAULT_LEGEND_PROPS,
    DEFAULT_METRICS_PROPS,
    DEFAULT_NAVIGATOR_PROPS,
    DEFAULT_STACKED_PROPS,
    METRICS_DROPDOWN_OPTIONS,
} from "./consts";
import {
    EXCLUDE_STABLECOINS_FILTER,
    METRIC_INTERVALS,
    METRIC_TO_FILTER,
} from "./getApiQuery";
import {
    DistributionSelectProps,
    GroupByProps,
    IntervalProps,
    LegendProps,
    MetricsProps,
    MetricTypes,
    NavigatorProps,
    StackedProps,
} from "./types";
import { ColumnSpanConfig } from "~/types";

// // // //

/** Group id the API uses for rows that don't belong to any group */
const NONE_GROUP_ID = 0;

/**
 * Metrics whose "None" group is kept. Fund flows are grouped by measure and
 * APY comes from the asset endpoint, so neither produces a meaningful None row
 * (mirrors getTimeSeriesViews).
 */
const METRICS_KEEPING_NONE_GROUP: MetricTypes[] = [
    MetricTypes.MintBurnVolume,
    MetricTypes.Apy7Day,
];

/**
 * Groups whose Total Value series is reported sparsely. Gaps are filled with
 * the previous value so the area chart doesn't collapse between reports
 * (mirrors getTimeSeriesViews).
 */
const INTERPOLATED_GROUP_NAMES: string[] = [
    BROADRIDGE_PLATFORM_NAME,
    CANTON_NETWORK_NAME,
    REPURCHASE_AGREEMENTS_ASSET_CLASS_NAME,
];

/**
 * Maps each measure slug a mode can return back to its metric. The data
 * transform below only sees fetched rows, not the active mode, so it infers
 * the metric from the row's measure.
 */
const MEASURE_SLUG_TO_METRIC: Record<string, MetricTypes> = {
    [MEASURE_SLUGS.bridged_token_value_dollar]: MetricTypes.TotalValue,
    [MEASURE_SLUGS.bridged_token_market_cap_dollar]: MetricTypes.TotalValue,
    [MEASURE_SLUGS.holding_addresses_count]: MetricTypes.AssetHolders,
    [MEASURE_SLUGS.monthly_active_addresses_count]: MetricTypes.ActiveAddresses,
    [MEASURE_SLUGS.daily_transfer_volume_dollar]: MetricTypes.TransferVolume,
    [MEASURE_SLUGS.adjusted_daily_mints_dollar]: MetricTypes.MintBurnVolume,
    [MEASURE_SLUGS.adjusted_daily_burns_dollar]: MetricTypes.MintBurnVolume,
    [MEASURE_SLUGS.apy_7_day]: MetricTypes.Apy7Day,
};

// // // //

/**
 * transformMetricTimeseriesData
 * Reshapes fetched rows the way getTimeSeriesViews does for MetricTimeseriesChart:
 * drops the "None" group (except for fund flows + APY) and fills date gaps for
 * sparsely-reported Total Value groups.
 */
export function transformMetricTimeseriesData(
    data: TimeSeriesItem[],
): TimeSeriesItem[] {
    return data
        .filter((each) => {
            const metric = MEASURE_SLUG_TO_METRIC[each.measure.slug];
            if (
                metric === undefined ||
                METRICS_KEEPING_NONE_GROUP.includes(metric)
            ) {
                return true;
            }
            return each.group.id !== NONE_GROUP_ID;
        })
        .map((each) => {
            const metric = MEASURE_SLUG_TO_METRIC[each.measure.slug];
            if (
                metric === MetricTypes.TotalValue &&
                INTERPOLATED_GROUP_NAMES.includes(each.group.name)
            ) {
                return interpolateMissingDates(each);
            }
            return each;
        });
}

// // // //

/**
 * getMetricFilter
 * Builds the filter for a metric's mode query.
 *
 * Total Value is special: stablecoins report their value under the market-cap
 * measure, so their rows come in through a second OR branch. That branch only
 * yields rows while stablecoins are included. VisualizationModeCard ANDs the
 * exclude-stablecoins filter onto the whole query otherwise, which zeroes it
 * out, so the static query stays correct for every toggle state.
 */
function getMetricFilter(props: {
    metric: MetricTypes;
    filters: Filter[];
}): Filter {
    const { metric, filters } = props;

    if (metric === MetricTypes.MintBurnVolume) {
        return {
            operator: FilterOperators.And,
            filters: filters,
        };
    }

    const metricFilter = METRIC_TO_FILTER[metric];
    const appliedFilters = metricFilter ? [metricFilter, ...filters] : filters;

    if (metric !== MetricTypes.TotalValue) {
        return {
            operator: FilterOperators.And,
            filters: appliedFilters,
        };
    }

    return {
        operator: FilterOperators.Or,
        filters: [
            {
                operator: FilterOperators.And,
                filters: [...appliedFilters, EXCLUDE_STABLECOINS_FILTER],
            },
            {
                operator: FilterOperators.And,
                filters: [
                    {
                        operator: FilterOperators.Equals,
                        field: TokenTimeseriesColumns.assetClassID,
                        value: ASSET_CLASS_IDS.Stablecoin,
                    },
                    {
                        operator: FilterOperators.Equals,
                        field: TokenTimeseriesColumns.measureSlug,
                        value: MEASURE_SLUGS.bridged_token_market_cap_dollar,
                    },
                    ...filters,
                ],
            },
        ],
    };
}

// // // //

interface GetMetricTimeseriesModesProps {
    analyticsChartName: string;
    metrics: MetricTypes[];
    filters: Filter[];
    height: number;
    legend: LegendProps;
    navigator: NavigatorProps;
    stacked: StackedProps;
    groupBy: GroupByProps;
}

/**
 * getMetricTimeseriesModes
 * Builds one VisualizationMode per metric, reproducing the query and chart
 * config MetricTimeseriesChart derives from the selected metric.
 */
function getMetricTimeseriesModes(
    props: GetMetricTimeseriesModesProps,
): VisualizationMode[] {
    return props.metrics.map((metric) =>
        getMetricTimeseriesMode({ ...props, metric }),
    );
}

function getMetricTimeseriesMode(
    props: GetMetricTimeseriesModesProps & { metric: MetricTypes },
): VisualizationMode {
    const {
        analyticsChartName,
        metric,
        filters,
        height,
        legend,
        navigator,
        stacked,
        groupBy,
    } = props;

    const option = METRICS_DROPDOWN_OPTIONS.find(
        (each) => each.value === metric,
    );

    const isApy = metric === MetricTypes.Apy7Day;
    const isMintBurn = metric === MetricTypes.MintBurnVolume;
    const isFlow = isMintBurn || metric === MetricTypes.TransferVolume;
    const isColumn =
        metric === MetricTypes.ActiveAddresses ||
        metric === MetricTypes.TransferVolume;

    // MetricTimeseriesChart hides the stacked + group-by controls for fund
    // flows and APY, and never stacks either chart
    const showAggregateControls = !isApy && !isMintBurn;

    // Fund flows are always grouped by measure so mints + burns come back as
    // two series
    const groupByValue = (
        isMintBurn ? TokenTimeseriesGroupBy.measure : groupBy.value
    ) as TokenTimeseriesGroupBy;

    const groupByOptions = (
        isMintBurn
            ? [TokenTimeseriesGroupBy.measure]
            : (groupBy.allowedOptions ?? [])
    ) as TokenTimeseriesGroupBy[];

    // The chart-level legend config is everything but the control state
    const {
        enabled: _legendEnabled,
        active: _legendActive,
        ...legendConfig
    } = legend;

    let type = VisualizationModeTypes.area;
    if (isMintBurn) {
        type = VisualizationModeTypes.mintBurnVolume;
    } else if (isApy) {
        type = VisualizationModeTypes.apy;
    } else if (isColumn) {
        type = VisualizationModeTypes.column;
    }

    return {
        key: `${analyticsChartName}-${metric}`,
        type,
        content: {
            label: option?.label ?? metric,
        },
        loginRequired: option?.loginRequired ?? false,
        endpoint: isApy
            ? ApiQueryEndpoints.AssetTimeseriesV4
            : isMintBurn
              ? ApiQueryEndpoints.FundFlowsTimeseriesV4
              : ApiQueryEndpoints.TokenTimeseriesV4,
        query: {
            sort: {
                direction: SortDirections.desc,
                field: "date",
            },
            pagination: {
                page: 1,
                perPage: 100,
            },
            aggregate: {
                groupBy: groupByValue,
                aggregateFunction: AggregateFunctions.Sum,
                interval: METRIC_INTERVALS[metric],
                groupLimit: 50,
                mode: isFlow
                    ? AggregateQueryMode.flow
                    : AggregateQueryMode.stock,
            },
            filter: getMetricFilter({ metric, filters }),
        },
        // forceUpdate so each metric's group-by + interval are re-applied on
        // every switch. The mode state manager would otherwise carry the
        // previous metric's values into this query.
        queryControls: {
            groupBy: {
                enabled:
                    showAggregateControls &&
                    !!groupBy.enabled &&
                    !!groupBy.allowedOptions,
                forceUpdate: true,
                value: groupByValue,
                allowedOptions: groupByOptions,
                labelOverrides: groupBy.labelOverrides as
                    Partial<Record<TokenTimeseriesGroupBy, string>> | undefined,
            },
            // The interval is pinned per metric, as in MetricTimeseriesChart
            // (controls.interval is unused there too)
            interval: {
                enabled: false,
                forceUpdate: true,
                value: METRIC_INTERVALS[metric],
                allowedOptions: [METRIC_INTERVALS[metric]],
            },
        },
        visualizationConfig: {
            area: !isApy,
            showTotal: !isMintBurn && !isApy,
            height,
            // MetricTimeseriesChart never pins the y-axis minimum (its `min`
            // expression always evaluates to undefined). Keep the axis
            // auto-scaled so fund flow burns + APY dips render below zero.
            yAxis: {
                min: undefined,
                enabled: true,
            },
            linePlotOptions: {
                marker: {
                    enabled: false,
                    states: {
                        hover: {
                            enabled: true,
                        },
                    },
                },
            },
            legend: {
                ...legendConfig,
                // Fund flows never show a legend
                ...(isMintBurn ? { enabled: false } : {}),
            },
        },
        visualizationControls: {
            // No forceUpdate: the legend + navigator toggles persist across
            // metrics, as in MetricTimeseriesChart
            legend: {
                enabled: legend.enabled,
                active: legend.active,
                forceUpdate: false,
            },
            navigator: {
                enabled: navigator.enabled,
                active: navigator.active,
                forceUpdate: false,
                xAxis: navigator.xAxis,
            },
            // APY lines and fund flow columns must never stack, so the toggle
            // is reset on entry to those modes
            stacked: showAggregateControls
                ? {
                      enabled: stacked.enabled,
                      active: stacked.active,
                      forceUpdate: false,
                  }
                : {
                      enabled: false,
                      active: false,
                      forceUpdate: true,
                  },
        },
        apiTools: isApy
            ? ASSET_TIMESERIES_V4_API_TOOLS_CONTROLS
            : isMintBurn
              ? FUND_FLOWS_TIMESERIES_V4_API_TOOLS_CONTROLS
              : TOKEN_TIMESERIES_V4_API_TOOLS_CONTROLS,
        dataExportControls: {
            enabled: true,
            // Total Value downloads are open to every logged-in user; the rest
            // need the data-downloads feature flag
            ignoreFeatureFlag: metric === MetricTypes.TotalValue,
            exportTableName: isApy
                ? ExportTableNames.asset_timeseries_v4
                : isMintBurn
                  ? ExportTableNames.fund_flows_timeseries_v4
                  : ExportTableNames.token_timeseries_v4,
        },
    };
}

// // // //

interface Props {
    title?: string;
    columnSpanConfig?: ColumnSpanConfig;
    height?: number;
    filters?: Filter[];
    controls?: {
        navigator?: NavigatorProps;
        legend?: LegendProps;
        distributionSelect?: DistributionSelectProps;
        metrics?: MetricsProps;
        stacked?: StackedProps;
        groupBy?: GroupByProps;
        interval?: IntervalProps;
    };
    analyticsChartName: string;
}

/**
 * MetricTimeseriesChartV2
 * Drop-in replacement for MetricTimeseriesChart built on VisualizationModeCard.
 * Each metric becomes a visualization mode; the card owns the distribution
 * type + stablecoin controls and layers their filters onto the mode query.
 */
export function MetricTimeseriesChart(props: Props) {
    const {
        title = "Platform Metrics",
        height = 350,
        filters = [],
        columnSpanConfig = {
            sm: 12,
            md: 12,
            lg: 12,
            xl: 8,
            xxl: 8,
        },
        controls,
        analyticsChartName,
    } = props;

    const distributionSelect = {
        ...DEFAULT_DISTRIBUTION_SELECT_PROPS,
        ...controls?.distributionSelect,
    };

    const metrics = { ...DEFAULT_METRICS_PROPS, ...controls?.metrics };
    const legend = { ...DEFAULT_LEGEND_PROPS, ...controls?.legend };
    const navigator = { ...DEFAULT_NAVIGATOR_PROPS, ...controls?.navigator };
    const stacked = { ...DEFAULT_STACKED_PROPS, ...controls?.stacked };
    const groupBy = { ...DEFAULT_GROUP_BY_PROPS, ...controls?.groupBy };

    const modes = getMetricTimeseriesModes({
        analyticsChartName,
        metrics: metrics.options,
        filters,
        height,
        legend,
        navigator,
        stacked,
        groupBy,
    });

    return (
        <VisualizationModeCard
            title={title}
            modes={modes}
            columnSpanConfig={columnSpanConfig}
            distributionSelect={distributionSelect}
            transformTimeseriesData={transformMetricTimeseriesData}
        />
    );
}
